<template>
    <div class="classroom page-wrap" v-if="classroom">
        <div class="page-bar">
            <div>
                <div class="page-bar-sub">Work</div>
                <div class="page-bar-main">Training</div>
            </div>
        </div>
        <div :class="$store.state.routeLoaded ? 'slide-up view' : 'hide'">
            <div><router-link class="back-link" to="/classrooms"><svg-icon icon="leftarrow" />All Training</router-link></div>
            <h1>{{ classroom.Name }} <span :class="'tr-type ' + (classroom.Type == 'On-site' ? 'onsite' : 'classroom')"><svg-icon :icon="classroom.Type == 'On-site' ? 'wrench' : 'classroom'" />{{ classroom.Type == 'On-site' ? 'On-site' : 'Classroom' }}</span></h1>
            <div style="font-size:14px;margin: 5px 0 15px">{{ classroom.DateString }}</div>

            <onsite-session v-if="classroom.Type == 'On-site'" :read-only="!canManage" :classroom="classroom" :employees-all="employeesAll" :process-options="$store.state.allNodes" />

            <div v-else class="sess-grid">
                <div>
                    <div class="panel timer-panel">
                        <div v-if="classroom.Status == 'Upcoming'" class="timer-lbl">{{ canManage ? 'Select attendees to begin' : 'NOT STARTED YET' }}</div>
                        <div v-else-if="classroom.Status == 'In progress'" class="timer-lbl">SESSION RUNNING</div>
                        <div v-else-if="classroom.Status == 'Completed'" class="timer-lbl">FINAL TIME</div>
                        <div class="timer">{{ timeString }}</div>
                        <div v-if="classroom.Status == 'Upcoming' && canManage" class="timer-btns">
                            <button @click="() => { showStart = true }" class="btn-lg"><svg-icon icon="twousers" style="width:14px;height:14px" />Select attendees & start</button>
                        </div>
                        <div v-else-if="classroom.Status == 'In progress' && canManage" class="timer-btns">
                            <button v-if="paused" @click="pauseResumeTimer('stop')" class="btn-lg btn-light"><svg-icon icon="play2" style="width:12px;height:12px" />Resume</button>
                            <button v-else @click="pauseResumeTimer('start')" class="btn-lg btn-light"><svg-icon icon="pause" style="width:12px;height:12px" />Pause</button>
                            <button @click="endSession()" class="btn-lg btn-light"><svg-icon icon="check3" style="width:14px;height:14px" />End Session</button>
                        </div>
                        <div v-else-if="classroom.Status == 'Completed'" class="timer-btns">
                            <span class="pill p-done"><svg-icon icon="check2" />Session Complete</span>
                        </div>
                    </div>
                    <div class="panel">
                        <h4>Processes reviewed<span>{{ numFinished }}/{{ processes.length }}</span></h4>
                        <div class="bar">
                            <span :style="'width:'+finishedPercent"></span>
                        </div>
                        <div class="cl-processes">
                            <div class="checkbox-input" v-for="(p, i) in processes" :key="i">
                                <input type="checkbox" v-model="p.finished" @change="checkReviewedProcesses(true)" :disabled="!canManage" :style="classroom.Status != 'In progress' || !canManage ? 'pointer-events:none' : ''" />
                                <a @click.prevent="viewProcessFn(p)">{{ p.name }}</a>
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <div v-if="classroom.Status == 'Upcoming'" class="panel">
                        <h4>Attendees</h4>
                        <div style="padding:26px 12px 26px;text-align:center;font-size:14px">
                            <div><svg-icon icon="twousers" style="width:48px;height:48px;" /></div>
                            <div style="margin-bottom:5px;font-size:18px">No one selected yet.</div>
                            <div v-if="canManage" style="margin-bottom:15px">Pick who's in the room when you're ready to start.</div>
                            <div v-if="canManage">
                                <button @click="() => { showStart = true }" class="btn-lg" style="margin:0"><svg-icon icon="twousers" style="width:14px;height:14px" />Select attendees</button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="classroom.Status == 'In progress'" class="panel">
                        <h4>Attendees</h4>
                        <div v-for="(a, i) in attendeesList" :key="i" class="att" :style="a.paused ? 'opacity: .5' : ''">
                            <div class="av">{{ a.initials }}</div>
                            <div class="att-n">
                                <div style="color:var(--dark-gray)">{{ a.name }}</div>
                                <div style="font-size:12px">{{ a.jobTitle }}</div>
                            </div>
                            <span class="att-t">{{ a.timeString }}</span>
                            <button v-if="a.paused && canManage" @click="pauseResumeTimerAtt('stop', a)" :style="paused ? 'pointer-events:none' : ''" class="toggle"><svg-icon icon="minususer" /></button>
                            <button v-else-if="canManage" :style="paused ? 'pointer-events:none' : ''" @click="pauseResumeTimerAtt('start', a)" class="toggle"><svg-icon icon="adduser" /></button>
                        </div>
                        <div v-if="canManage" style="margin-top:15px">
                            <a @click.prevent="() => { showStart = true }" class="primary" style="text-decoration:none!important;"><svg-icon icon="adduser" style="width:14px; height:14px" />&nbsp;&nbsp;Edit attendees</a>
                        </div>
                    </div>
                    <div v-else-if="classroom.Status == 'Completed'" class="panel">
                        <h4>Attendees</h4>
                        <div v-for="(a, i) in attendeesList" :key="i" class="att">
                            <div class="av">{{ a.initials }}</div>
                            <div class="att-n">
                                <div style="color:var(--dark-gray)">{{ a.name }}</div>
                                <div style="font-size:12px">{{ a.jobTitle }}</div>
                            </div>
                            <span class="att-t">{{ a.timeString }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Team Hub admins, or the manager who created it (CanDelete, from the API) -->
            <div v-if="classroom.CanDelete" class="tr-delete"><a @click.prevent="showConfirmDelete = true">Delete Training</a></div>
        </div>

        <confirm-delete v-if="showConfirmDelete" :msg="`Delete '${classroom.Name}'? Its attendees, times and sign-offs are deleted with it.`" @confirm="deleteClassroom()" @cancel="showConfirmDelete = false" />

        <div v-if="showStart" class="error-msg-wrap">
            <div class="form-wrapper slide-up">
                <div class="form-close">
                    <button type="button" class="form-close-x" title="Close" @click="() => { showStart = false }">&times;</button>
                </div>
                <h3>Who's in the room?</h3>
                <div>Select everyone attending <b>{{ classroom.Name }}.</b> Time is recorded against each person you pick.</div>
                <input style="margin-top:15px" type="text" v-model="employeeSearchQ" @input="filterEmployees()" placeholder="Search employees" />
                <div style="max-height: 200px; overflow: auto;">
                    <div v-for="(e, i) in employees" :key="i" style="margin-bottom:5px" class="checkbox-input">
                        <input type="checkbox" v-model="e.checked" />
                        <div><span style="font-weight:600">{{ e.Name }} · </span><span style="font-size:14px">{{ e.SeatTitle }}</span></div>
                    </div>
                </div>
                <button style="margin-top:25px;width:100%" :class="disableButton ? 'btn-lg disable-button' : 'btn-lg'" @click="submitStart()"><svg-icon icon="play2" style="width:12px;height:12px" />Start Session</button>
            </div>
        </div>

        <pdf-pop-up v-if="viewProcess" :url="processSlug" @close="() => viewProcess = false" />
    </div>    
</template>

<script lang="ts">
import { defineComponent, registerRuntimeCompiler } from 'vue';
import axios from 'axios';
import SvgIcon from '@/components/SvgIcon.vue';
import PdfPopUp from '@/components/PDFPopUp.vue';
import OnsiteSession from '@/components/OnsiteSession.vue';
import ConfirmDelete from '@/components/ConfirmDelete.vue';

export default defineComponent({
    name: 'ClassroomView',
    components: {
        SvgIcon, PdfPopUp, OnsiteSession, ConfirmDelete
    },
    data() {
        return {
            classroom: null as any,
            showConfirmDelete: false,
            deleted: false, // set once it's deleted, here or on another device
            timeString: '00:00',
            processes: [] as any,
            numFinished: 0,
            finishedPercent: '0%',
            viewProcess: false,
            processSlug: null as any,
            showStart: false,
            employeesAll: [] as any,
            employees: [] as any,
            employeeSearchQ: '',
            disableButton: false,
            timerInterval: null as any,
            paused: false,
            attendeesList: [] as any,
            eventSource: null as any
        }
    },
    computed: {
        // Team Hub admins and managers run sessions (CanManage, from the API); everyone else just views them and can
        // open the listed processes
        canManage(): boolean {
            return !!this.classroom?.CanManage;
        }
    },
    async created() {
        while(!this.$store.state.allNodesLoaded) {
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        // Everyone in the company (ID, Name, SeatTitle), not /api/employees, which gives a manager only their direct reports:
        // saving attendees rebuilds the list from this one, so anyone missing from it would be dropped from the session
        await axios.get(`${this.$store.state.apiUrl}/api/employees/limitedinfo`)
            .then(resp => {
                this.employeesAll = resp.data;
                this.employees = this.employeesAll;
            })
            .catch(e => {
                console.log(e);
            })

        await axios.get(`${this.$store.state.apiUrl}/api/classroom/${this.$route.params.id}`)
            .then(resp => {
                const c = resp.data;
                const date = new Date(c.Date);
                c.month = this.$store.state.monthShortStrings[date.getUTCMonth() + 1].toUpperCase();
                c.date = date.getUTCDate();
                c.year = date.getUTCFullYear();
                if(c.ProcessesJSON) {
                    c.ProcessesJSON = JSON.parse(c.ProcessesJSON);
                }
                if(c.TimestampsJSON) {
                    c.TimestampsJSON = JSON.parse(c.TimestampsJSON);
                    if(c.TimestampsJSON.pauses && c.TimestampsJSON.pauses.length && c.TimestampsJSON.pauses[c.TimestampsJSON.pauses.length - 1].type == 'start') {
                        this.paused = true;
                    }
                }
                if(c.AttendeesJSON) {
                    c.AttendeesJSON = JSON.parse(c.AttendeesJSON);
                    for(const a of c.AttendeesJSON) {
                        const emp = this.employeesAll.find((e: any) => {
                            return e.ID == a.id;
                        });
                        if(emp) {
                            emp.checked = true;
                        }
                    }
                }
                c.DateString = new Date(c.Date).toLocaleDateString('en-US',{timeZone: 'UTC',weekday:'short',month:'short',day:'numeric',year:'numeric'});
                this.classroom = c;
                if(this.classroom.Type != 'On-site' && this.classroom.AttendeesJSON) {
                    this.setAttendeesList();
                }
            })
            .catch(e => {
                console.log(e);
            })

        if(!this.classroom) {
            alert('Classroom was not found');
            this.$router.push('/classrooms');
            return;
        }

        this.subscribeToUpdates();

        // On-site sessions have no timer/process-review checklist -- OnsiteSession.vue
        // handles everything else directly off the raw classroom prop.
        if(this.classroom.Type == 'On-site') {
            this.$store.state.routeLoaded = true;
            return;
        }

        this.processes = [];
        for(const p of this.classroom.ProcessesJSON) {
            const process = this.$store.state.allNodes.find((n: any) => {
                return n.Slug == p.slug;
            })
            if(process) {
                this.processes.push({
                    name: process.Name,
                    slug: process.Slug,
                    finished: p.finished ? true : false
                })
            }
        }

        this.checkReviewedProcesses(false);

        this.setTimeStrings();

        if(this.classroom.Status == 'In progress') {
            this.startTimer();
        }

        this.$store.state.routeLoaded = true;
    },
    beforeUnmount() {
        if(this.eventSource) {
            this.eventSource.close();
        }
        if(!this.classroom) {
            return;
        }
        if(this.timerInterval) {
            clearInterval(this.timerInterval);
        }
        // Not after it's been deleted (nothing left to pause), and only for someone who runs sessions (viewers can't save)
        if(this.classroom.Status == 'In progress' && !this.paused && !this.deleted && this.canManage) {
            this.pauseResumeTimer('start');
        }
    },
    methods: {
        // Observer/subscriber pattern: keep an SSE connection open to the classroom so that
        // whenever any device (this one included) POSTs a change to /api/classroom, every
        // open device gets the fresh record pushed down and updates its own view -- no polling.
        subscribeToUpdates() {
            const url = `${this.$store.state.apiUrl}/api/classroom/${this.classroom.ID}/subscribe`;
            this.eventSource = new EventSource(url, { withCredentials: true });
            this.eventSource.onmessage = (event: MessageEvent) => {
                let raw;
                try {
                    raw = JSON.parse(event.data);
                } catch(e) {
                    return;
                }
                this.applyRemoteUpdate(raw);
            };
            this.eventSource.onerror = (e: any) => {
                // EventSource retries the connection on its own; nothing to do here.
                console.log('classroom subscription error', e);
            };
        },
        async deleteClassroom() {
            this.showConfirmDelete = false;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom/${this.classroom.ID}/delete`)
                .then(() => {
                    this.deleted = true;
                    this.$router.push('/classrooms');
                })
                .catch(e => {
                    console.log(e);
                    alert(e?.response?.data || 'There was an error deleting this training');
                })
        },
        applyRemoteUpdate(raw: any) {
            if(!this.classroom || raw.ID != this.classroom.ID) {
                return;
            }
            // Deleted on another device (or this one): back to the list
            if(raw.Deleted) {
                this.deleted = true;
                if(this.$route.path.startsWith('/classroom/')) {
                    this.$router.push('/classrooms');
                }
                return;
            }
            this.classroom.Status = raw.Status;
            this.classroom.Completed = raw.Completed;
            this.classroom.Notes = raw.Notes;
            this.classroom.ProcessesJSON = raw.ProcessesJSON ? JSON.parse(raw.ProcessesJSON) : [];
            this.classroom.TimestampsJSON = raw.TimestampsJSON ? JSON.parse(raw.TimestampsJSON) : null;
            this.classroom.AttendeesJSON = raw.AttendeesJSON ? JSON.parse(raw.AttendeesJSON) : [];

            if(this.classroom.Type == 'On-site') {
                // OnsiteSession.vue owns its own copy of the classroom (see its `session` data) --
                // its prop watcher picks up the refreshed AttendeesJSON/ProcessesJSON from here.
                return;
            }

            if(this.classroom.TimestampsJSON && this.classroom.TimestampsJSON.pauses && this.classroom.TimestampsJSON.pauses.length) {
                this.paused = this.classroom.TimestampsJSON.pauses[this.classroom.TimestampsJSON.pauses.length - 1].type == 'start';
            } else {
                this.paused = false;
            }

            for(const process of this.classroom.ProcessesJSON) {
                const p = this.processes.find((pp: any) => { return pp.slug == process.slug });
                if(p) {
                    p.finished = process.finished ? true : false;
                }
            }
            this.checkReviewedProcesses(false);
            this.setAttendeesList();
            this.setTimeStrings();

            if(this.classroom.Status == 'In progress' && !this.timerInterval) {
                this.startTimer();
            } else if(this.classroom.Status != 'In progress' && this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }
        },
        async checkReviewedProcesses(submit: boolean) {
            for(const process of this.classroom.ProcessesJSON) {
                const finished = this.processes.find((p: any) => {
                    return p.slug == process.slug && p.finished;
                })
                process.finished = finished ? true : false;
            }
            this.numFinished = this.processes.filter((p: any) => { return p.finished }).length;
            this.finishedPercent = (this.numFinished / this.processes.length * 100).toFixed(2) + '%';
            if(submit) {
                await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.classroom)
                    .catch(e => {
                        console.log(e);
                    })
            }
        },
        viewProcessFn(p: any) {
            this.processSlug = '/playbook/' + p.slug + '?hideNav=t&hideComponent=t&hideChat=t';
            this.viewProcess = true;
        },
        filterEmployees() {
            if(this.employeeSearchQ.length < 3) {
                this.employees = this.employeesAll;
            } else {
                const searchQLower = this.employeeSearchQ.toLowerCase();
                this.employees = this.employeesAll.filter((e: any) => {
                    const eSearch = `${e.Name} ${e.SeatTitle || ''}`.toLowerCase();
                    return eSearch.includes(searchQLower);
                })
            }
        },
        setAttendeesList() {
            if(!this.classroom.AttendeesJSON) {
                return;
            }
            const attendeesList = [];
            for(const a of this.classroom.AttendeesJSON) {
                const emp = this.employeesAll.find((e: any) => {
                    return e.ID == a.id;
                });
                if(emp) {
                    const nameSplit = emp.Name.split(' ');
                    let initials = ``;
                    if(nameSplit[0]) {
                        initials += nameSplit[0].substring(0, 1);
                    }
                    if(nameSplit[1]) {
                        initials += nameSplit[1].substring(0, 1);
                    }
                    initials = initials.toUpperCase();
                    attendeesList.push({
                        id: emp.ID,
                        name: emp.Name,
                        jobTitle: emp.SeatTitle, // their role sheet seat's title when added (key kept: saved sessions use it)
                        initials: initials
                    })
                }
            }
            this.attendeesList = attendeesList;
        },
        async submitStart() {
            const selectedEmployees = this.employeesAll.filter((e: any) => {
                return e.checked;
            })
            if(!selectedEmployees.length) {
                alert('Select at least one employee');
                return;
            }
            let nowMS = new Date().getTime();
            let prevAttendees = [];
            if(this.classroom.AttendeesJSON) {
                prevAttendees = JSON.parse(JSON.stringify(this.classroom.AttendeesJSON));
            }
            this.classroom.AttendeesJSON = [];
            for(const e of selectedEmployees) {
                let newItem = {
                    id: e.ID,
                    timestampsJSON: {
                        start: nowMS
                    }
                }
                const prevItem = prevAttendees.find((a: any) => {
                    return a.id == e.ID;
                })
                if(prevItem) {
                    newItem = prevItem;
                }
                this.classroom.AttendeesJSON.push(newItem);
            }
            // TODO keep previous AttendeesJSON pauses if resetting attendees
            this.setAttendeesList();
            if(this.classroom.Status == 'Upcoming') {
                this.classroom.Status = 'In progress';
                this.classroom.TimestampsJSON = {
                    start: nowMS
                }
            }            
            this.disableButton = true;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.classroom)
                .then(resp => {
                    this.showStart = false;
                    this.startTimer();
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })
            this.disableButton = false;
        },
        setTimeStrings() {
            if(!this.classroom.TimestampsJSON) {
                return;
            }
            let nowMS = new Date().getTime();
            this.setTimeString(this.classroom.TimestampsJSON, this, nowMS);
            for(const a of this.classroom.AttendeesJSON) {
                const att = this.attendeesList.find((attl: any) => {
                    return a.id == attl.id;
                })
                this.setTimeString(a.timestampsJSON, att || a, nowMS);
            }
            this.$forceUpdate();
        },
        setTimeString(timestampsJSON: any, timeStringObj: any, nowMS: number) {
            let now = nowMS;
            let pauses = [];
            if(timestampsJSON.pauses && timestampsJSON.pauses.length) {
                // currently in a pause
                if(timestampsJSON.pauses[timestampsJSON.pauses.length - 1].type == 'start') {
                    now = timestampsJSON.pauses[timestampsJSON.pauses.length - 1].ms;
                }
                for(let i = 0; i < timestampsJSON.pauses.length; i += 2) {
                    if(timestampsJSON.pauses[i + 1]) {
                        pauses.push(timestampsJSON.pauses[i + 1].ms - timestampsJSON.pauses[i].ms);
                    }
                }
            }
            if(timestampsJSON.end) {
                now = timestampsJSON.end;
            }
            let ms = now - timestampsJSON.start;
            for(const pause of pauses) {
                ms -= pause;
            }
            const hours = ('' + Math.floor(ms / (1000 * 60 * 60))).padStart(2, '0');
            const minutes = ('' + Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
            const seconds = ('' + Math.floor((ms % (1000 * 60)) / 1000)).padStart(2, '0');
            if(hours != '00') {
                timeStringObj.timeString = `${hours}:${minutes}:${seconds}`
            } else {
                timeStringObj.timeString = `${minutes}:${seconds}`
            }
        },
        startTimer() {
            if(this.timerInterval) {
                clearInterval(this.timerInterval);
            }
            this.timerInterval = setInterval(() => {
                if(this.paused) {
                    return;
                }
                this.setTimeStrings();
            }, 300);
        },
        async pauseResumeTimer(type: string) {
            this.paused = type == 'start';
            let nowMS = new Date().getTime();
            if(!this.classroom.TimestampsJSON.pauses) {
                this.classroom.TimestampsJSON.pauses = [];
            }
            this.classroom.TimestampsJSON.pauses.push({
                type: type,
                ms: nowMS
            })
            for(const a of this.classroom.AttendeesJSON) {
                if(a.paused && this.paused) {
                    continue;
                }
                if(!a.timestampsJSON.pauses) {
                    a.timestampsJSON.pauses = [];
                }
                a.timestampsJSON.pauses.push({
                    type: type,
                    ms: nowMS
                })
                a.paused = this.paused;
                const att = this.attendeesList.find((a2: any) => {
                    return a2.id == a.id;
                })
                if(att) {
                    att.paused = a.paused;
                }
            }
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.classroom)
                .catch(e => {
                    console.log(e);
                })
        },
        async pauseResumeTimerAtt(type: string, a: any) {
            const att = this.classroom.AttendeesJSON.find((a2: any) => {
                return a2.id == a.id;
            })
            if(!att) {
                return;
            }
            a.paused = type == 'start';
            att.paused = a.paused;
            let nowMS = new Date().getTime();
            if(!att.timestampsJSON.pauses) {
                att.timestampsJSON.pauses = [];
            }
            att.timestampsJSON.pauses.push({
                type: type,
                ms: nowMS
            })
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.classroom)
                .catch(e => {
                    console.log(e);
                })
        },
        async endSession() {
            if(this.numFinished != this.processes.length) {
                alert('Be sure to review all of the processes before ending this session.');
                return;
            }
            let nowMS = new Date().getTime();
            this.classroom.TimestampsJSON.end = nowMS;
            for(const a of this.classroom.AttendeesJSON) {
                a.timestampsJSON.end = nowMS;
            }
            this.classroom.Status = 'Completed';
            this.classroom.Completed = true;
            await axios.post(`${this.$store.state.apiUrl}/api/classroom`, this.classroom)
                .catch(e => {
                    console.log(e);
                })
        }
    }
})
</script>